import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./css/LoginFormModal.css"; // Reuse existing modal styles
import ForgotPasswordAPI from "../services/ForgotPasswordAPI";
import { validatePassword } from "../utils/PasswordUtil";
import PasswordStrengthMeter from "../utils/PasswordStrengthMeter";

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  // Check password match in real-time as user types
  const checkPasswordMatch = (confirmValue) => {
    if (confirmValue === "") {
      setPasswordMismatch(false);
      return;
    }
    // Check if confirm password matches new password character by character up to current keystroke
    const matches = newPassword.startsWith(confirmValue);
    setPasswordMismatch(!matches);
  };

  // Validate the reset token when component mounts
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setErrorMessage(
          "Invalid reset link. Please request a new password reset."
        );
        setIsValidating(false);
        return;
      }

      try {
        await ForgotPasswordAPI.validateResetToken(token);
        setIsTokenValid(true);
      } catch (error) {
        console.error("Error validating reset token:", error);

        if (error.response?.data?.message) {
          setErrorMessage(error.response.data.message);
        } else {
          setErrorMessage(
            "This reset link is invalid or has expired. Please request a new password reset."
          );
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validate password strength using NIST-compliant PasswordUtil
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setErrorMessage(passwordValidation.message);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      setIsLoading(true);
      await ForgotPasswordAPI.resetPassword(token, newPassword);

      setSuccessMessage(
        "Your password has been successfully reset. You can now log in with your new password."
      );

      // Redirect to home page after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error("Error resetting password:", error);

      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage(
          "Failed to reset password. Please try again or request a new reset link."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  if (isValidating) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-content">
          <h2>Validating Reset Link</h2>
          <p>Please wait while we validate your reset link...</p>
        </div>
      </div>
    );
  }

  if (!isTokenValid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-content">
          <h2>Invalid Reset Link</h2>
          <p
            className="error-message"
            style={{ visibility: "visible", opacity: 1 }}
          >
            {errorMessage}
          </p>
          <button className="login-button" onClick={handleBackToHome}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-content">
          <h2>Password Reset Successful</h2>
          <p
            className="success-message"
            style={{ color: "green", fontWeight: "bold" }}
          >
            {successMessage}
          </p>
          <p>You will be redirected to the home page in a moment...</p>
          <button className="login-button" onClick={handleBackToHome}>
            Go to Home Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-content">
        <h2>Reset Your Password</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              disabled={isLoading}
              minLength={12}
              maxLength={128}
              style={{
                borderColor: errorMessage ? "lightcoral" : "#ddd",
              }}
              required
            />
            <PasswordStrengthMeter password={newPassword} />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                const value = e.target.value;
                setConfirmPassword(value);
                checkPasswordMatch(value);
              }}
              placeholder="Confirm your new password"
              disabled={isLoading}
              minLength={12}
              maxLength={128}
              style={{
                borderColor: passwordMismatch ? "lightcoral" : "#ddd",
              }}
              required
            />
            {passwordMismatch && confirmPassword && (
              <small
                style={{
                  color: "lightcoral",
                  display: "block",
                  marginTop: "4px",
                }}
              >
                Passwords do not match
              </small>
            )}
            {!passwordMismatch &&
              confirmPassword &&
              newPassword &&
              confirmPassword === newPassword && (
                <small
                  style={{
                    color: "#28a745",
                    display: "block",
                    marginTop: "4px",
                    fontWeight: "600",
                  }}
                >
                  ✓ Passwords match
                </small>
              )}
          </div>

          <div className="password-requirements">
            <p>
              <strong>Password Requirements:</strong>
            </p>
            <ul>
              <li>
                Minimum 12 characters (15+ recommended for better security)
              </li>
              <li>Use a memorable passphrase instead of complex characters</li>
              <li>
                Avoid common words, sequential patterns, or repeated characters
              </li>
              <li>Example: "coffee-morning-sunshine-blue"</li>
            </ul>
            <p
              style={{
                fontSize: "0.85rem",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Strong passphrases are easier to remember and more secure than
              complex short passwords!
            </p>
          </div>

          {errorMessage && (
            <p
              className="error-message"
              style={{ visibility: "visible", opacity: 1 }}
            >
              {errorMessage}
            </p>
          )}

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <div className="footer-links">
          <a href="#home" onClick={handleBackToHome}>
            Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
